import {
  CaretRight,
  ClockCounterClockwise,
  Envelope,
  Faders,
  GithubLogo,
  type Icon,
  Key,
  Shield,
  ShieldCheck,
  UserPlus,
  Users,
  UsersThree,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  authInfoQuery,
  githubReposQuery,
  groupsQuery,
  invitesQuery,
  recentAuditQuery,
  rolesQuery,
  settingsQuery,
  usersQuery,
} from "@/components/admin/api";
import { auditActionLabel } from "@/components/admin/audit";
import { useAdminCaps } from "@/components/admin/caps";
import { plural } from "@/components/admin/shared";
import { useBreadcrumbs } from "@/components/shell/breadcrumbs";
import { Page } from "@/components/shell/Page";
import { Badge, RelativeTime, SectionHeader, Skeleton, StatTile } from "@/components/ui";
import { cx } from "@/lib/cx";

export const Route = createFileRoute("/_app/admin/")({ component: Overview });

function Overview() {
  useBreadcrumbs([{ label: m.adm_title() }]);
  const caps = useAdminCaps();

  return (
    <Page title={m.adm_title()} icon={ShieldCheck} meta={m.adm_overview_meta()} width="full" className="max-w-5xl">
      <div className="flex flex-col gap-8">
        {caps.users ? <Counts /> : null}
        <SetupList caps={caps} />
        {caps.audit ? <RecentActivity /> : null}
      </div>
    </Page>
  );
}

function Counts() {
  const users = useQuery(usersQuery);
  const invites = useQuery(invitesQuery);
  const groups = useQuery(groupsQuery);
  const roles = useQuery(rolesQuery);
  const people = users.data?.filter((u) => u.kind === "human" && !u.disabledAt).length;
  const bots = users.data?.filter((u) => u.kind === "bot" && !u.disabledAt).length;
  const pending = invites.data?.filter((i) => i.status === "pending").length;
  const custom = roles.data?.filter((r) => !r.builtin).length;

  return (
    <section aria-label={m.adm_overview_counts()} className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile
        label={m.adm_nav_users()}
        icon={Users}
        to="/admin/users"
        value={users.isError ? "-" : people}
        hint={bots === undefined ? undefined : plural(bots, m.adm_overview_agents_one, m.adm_overview_agents)}
      />
      <StatTile
        label={m.adm_overview_pending_invites()}
        icon={UserPlus}
        to="/admin/invites"
        value={invites.isError ? "-" : pending}
      />
      <StatTile
        label={m.adm_nav_groups()}
        icon={UsersThree}
        to="/admin/groups"
        value={groups.isError ? "-" : groups.data?.length}
      />
      <StatTile
        label={m.adm_nav_roles()}
        icon={Shield}
        to="/admin/roles"
        value={roles.isError ? "-" : roles.data?.length}
        hint={custom === undefined ? undefined : m.adm_overview_custom_roles({ count: custom })}
      />
    </section>
  );
}

type Row = { to: string; icon: Icon; title: string; description: string; status?: ReactNode };

function SetupList({ caps }: { caps: ReturnType<typeof useAdminCaps> }) {
  const auth = useQuery({ ...authInfoQuery, enabled: caps.instance });
  const settings = useQuery({ ...settingsQuery, enabled: caps.instance });
  const repos = useQuery({ ...githubReposQuery, enabled: caps.instance });
  const invites = useQuery({ ...invitesQuery, enabled: caps.users });
  const pending = invites.data?.filter((i) => i.status === "pending").length ?? 0;

  const rows: Row[] = [];
  if (caps.users) {
    rows.push({
      to: "/admin/invites",
      icon: UserPlus,
      title: m.adm_overview_invite_title(),
      description: m.adm_overview_invite_body(),
      status:
        pending > 0 ? (
          <Badge tone="accent" numeric>
            {m.adm_overview_n_pending({ count: pending })}
          </Badge>
        ) : undefined,
    });
    rows.push({
      to: "/admin/access",
      icon: Key,
      title: m.adm_overview_access_title(),
      description: m.adm_overview_access_body(),
    });
  }
  if (caps.instance) {
    const sso = auth.data?.oidc;
    rows.push({
      to: "/admin/authentication",
      icon: ShieldCheck,
      title: m.adm_nav_authentication(),
      description: m.adm_overview_auth_body(),
      status: !sso ? undefined : sso.active ? (
        <Badge tone="success">{m.adm_overview_sso_on()}</Badge>
      ) : sso.configured ? (
        <Badge tone="warning">{m.adm_overview_sso_down()}</Badge>
      ) : (
        <Badge>{m.adm_overview_sso_off()}</Badge>
      ),
    });
    rows.push({
      to: "/admin/instance",
      icon: Faders,
      title: m.adm_overview_2fa_title(),
      description: m.adm_overview_2fa_body(),
      status: !settings.data ? undefined : settings.data.require2faForAdmins ? (
        <Badge tone="success">{m.ui_on()}</Badge>
      ) : (
        <Badge tone="warning">{m.ui_off()}</Badge>
      ),
    });
    rows.push({
      to: "/admin/authentication",
      icon: Envelope,
      title: m.adm_overview_mail_title(),
      description: auth.data && !auth.data.mail.smtpConfigured ? m.adm_overview_mail_off() : m.adm_overview_mail_body(),
      status: !auth.data ? undefined : auth.data.mail.smtpConfigured ? (
        <Badge tone="success">{m.ui_on()}</Badge>
      ) : (
        <Badge tone="warning">{m.ui_off()}</Badge>
      ),
    });
    rows.push({
      to: "/admin/github",
      icon: GithubLogo,
      title: m.adm_nav_github(),
      description: m.adm_overview_github_body(),
      status: repos.data ? <Badge numeric>{m.adm_overview_repos({ count: repos.data.length })}</Badge> : undefined,
    });
  }
  if (rows.length === 0) return null;

  return (
    <section aria-labelledby="adm-setup">
      <SectionHeader id="adm-setup" title={m.adm_overview_setup()} className="mb-3" />
      <ul className="m-0 flex list-none flex-col divide-y divide-line rounded-panel border border-line bg-surface p-0 shadow-[0_1px_2px_var(--shadow-soft)]">
        {rows.map((r) => (
          <li key={r.title}>
            <Link
              to={r.to}
              className="transition-ui group flex items-center gap-3 px-4 py-3 outline-none first:rounded-t-panel last:rounded-b-panel hover:bg-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid focus-visible:outline-focus"
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-control border border-line bg-canvas text-fg-muted">
                <r.icon size={16} aria-hidden="true" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-medium text-fg">{r.title}</span>
                <span className="text-xs text-fg-muted">{r.description}</span>
              </span>
              {r.status}
              <CaretRight size={14} aria-hidden="true" className="shrink-0 text-fg-subtle group-hover:text-fg-muted" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RecentActivity() {
  const q = useQuery(recentAuditQuery);
  return (
    <section aria-labelledby="adm-recent">
      <SectionHeader
        id="adm-recent"
        title={m.adm_overview_recent()}
        className="mb-3"
        actions={
          <Link
            to="/admin/audit"
            className="rounded-control px-1 text-sm text-accent-text underline-offset-2 hover:underline"
          >
            {m.adm_overview_view_audit()}
          </Link>
        }
      />
      {q.isError ? null : q.isPending ? (
        <div className="flex flex-col gap-2" role="status" aria-busy="true">
          <span className="sr-only">{m.common_loading()}</span>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-line rounded-panel border border-line bg-surface p-0 shadow-[0_1px_2px_var(--shadow-soft)]">
          {(q.data ?? []).map((e) => (
            <li key={e.id} className={cx("flex items-center gap-3 px-4 py-2.5 text-sm")}>
              <ClockCounterClockwise size={14} aria-hidden="true" className="shrink-0 text-fg-subtle" />
              <span className="min-w-0 flex-1 truncate-title">
                <span className="font-medium text-fg">{auditActionLabel(e.action)}</span>{" "}
                <span className="text-fg-muted">
                  {m.adm_invites_by({ name: e.actor?.name ?? m.adm_system_actor() })}
                </span>
              </span>
              <RelativeTime value={e.createdAt} className="shrink-0 text-xs text-fg-subtle" />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
